import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { PopoverOrigin } from '@mui/material/Popover';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import routes from '../../config/routes';
import { useIsCurrentPath } from '../../hooks';
import { UserAvatar } from '../../components';
import InteractiveGuide, { type GuideStep } from '../../components/InteractiveGuide';

const idButton = 'header-options-button';
const idMenu = 'header-options-menu';
const menuListProps = {
  'aria-labelledby': idButton,
};

const popoverOrigin: PopoverOrigin = { vertical: 'top', horizontal: 'right' };

type ButtonSettingProps = { handleClose: () => void };

const ButtonSetting = ({ handleClose }: ButtonSettingProps) => {
  const { t } = useTranslation();
  const showSettings = !useIsCurrentPath(routes.protectedRoutes.profile.path);

  return showSettings ? (
    <MenuItem component={Link} to={routes.protectedRoutes.profile.path} onClick={handleClose}>
      {t('layout.header.menu.settings')}
    </MenuItem>
  ) : null;
};

const MenuHeader = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick: IconButtonProps['onClick'] = event => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleClickLogout = () => {
    handleClose();
    instance.logoutRedirect();
  };

  const guideSteps: GuideStep[] = [
    {
      selector: '[data-guide="top-header-bar"]',
      title: t('layout.guide.steps.headerBar.title'),
      description: t('layout.guide.steps.headerBar.description'),
    },
    {
      selector: '[data-guide="menu-toggle-button"]',
      title: t('layout.guide.steps.menu.title'),
      description: t('layout.guide.steps.menu.description'),
    },
    {
      selector: '[data-guide="help-guide-button"]',
      title: t('layout.guide.steps.helpButton.title'),
      description: t('layout.guide.steps.helpButton.description'),
    },
    {
      selector: '[data-guide="sidebar-navigation"]',
      title: t('layout.guide.steps.sidebar.title'),
      description: t('layout.guide.steps.sidebar.description'),
    },
    {
      selector: '[data-guide="dashboard-refresh-panel"]',
      title: t('layout.guide.steps.dashboardRefresh.title'),
      description: t('layout.guide.steps.dashboardRefresh.description'),
    },
    {
      selector: '[data-guide="country-conciliation-card"]',
      title: t('layout.guide.steps.countryCard.title'),
      description: t('layout.guide.steps.countryCard.description'),
    },
    {
      selector: '[data-guide="country-conciliation-table"]',
      title: t('layout.guide.steps.countryTable.title'),
      description: t('layout.guide.steps.countryTable.description'),
    },
    {
      selector: '[data-guide="dashboard-fiscal-import-card"]',
      title: t('layout.guide.steps.fiscalImport.title'),
      description: t('layout.guide.steps.fiscalImport.description'),
    },
    {
      selector: '[data-guide="dashboard-master-import-card"]',
      title: t('layout.guide.steps.mastersImport.title'),
      description: t('layout.guide.steps.mastersImport.description'),
    },
    {
      selector: '[data-guide="dashboard-status-cards"]',
      title: t('layout.guide.steps.statusCards.title'),
      description: t('layout.guide.steps.statusCards.description'),
    },
    {
      selector: '[data-guide="dashboard-totals-yesterday"]',
      title: t('layout.guide.steps.totalsYesterday.title'),
      description: t('layout.guide.steps.totalsYesterday.description'),
    },
    {
      selector: '[data-guide="dashboard-totals-currentMonth"]',
      title: t('layout.guide.steps.totalsCurrentMonth.title'),
      description: t('layout.guide.steps.totalsCurrentMonth.description'),
    },
    {
      selector: '[data-guide="dashboard-totals-lastMonth"]',
      title: t('layout.guide.steps.totalsLastMonth.title'),
      description: t('layout.guide.steps.totalsLastMonth.description'),
    },
    {
      selector: '[data-guide="dashboard-closure-evolution"]',
      title: t('layout.guide.steps.closureEvolution.title'),
      description: t('layout.guide.steps.closureEvolution.description'),
    },
    {
      selector: '[data-guide="report-filters-panel"]',
      title: t('layout.guide.steps.filtersSection.title'),
      description: t('layout.guide.steps.filtersSection.description'),
    },
    {
      selector: '[data-guide="report-table-panel"]',
      title: t('layout.guide.steps.tableSection.title'),
      description: t('layout.guide.steps.tableSection.description'),
      preferredPlacement: 'overlay-top-left',
      panelSize: 'regular',
    },
    {
      selector: '[data-guide="profile-menu-button"]',
      title: t('layout.guide.steps.profile.title'),
      description: t('layout.guide.steps.profile.description'),
    },
  ];

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton
          aria-label={t('layout.header.menu.helpCenter', { defaultValue: 'Instructivo' })}
          color="inherit"
          onClick={() => navigate(routes.protectedRoutes.helpCenter.path)}
        >
          <MenuBookOutlinedIcon
            sx={{
              color: theme => theme.palette.primary.main,
            }}
          />
        </IconButton>
        <IconButton
          aria-label={t('layout.header.menu.help')}
          color="inherit"
          onClick={() => setIsGuideOpen(true)}
          data-guide="help-guide-button"
        >
          <HelpOutlineIcon
            sx={{
              color: theme => theme.palette.primary.main,
            }}
          />
        </IconButton>
        <IconButton
          id={idButton}
          aria-label="more"
          aria-controls={open ? idMenu : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          color="inherit"
          data-guide="profile-menu-button"
        >
          <UserAvatar />
        </IconButton>
      </Stack>
      <Menu
        id={idMenu}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={menuListProps}
        anchorOrigin={popoverOrigin}
        transformOrigin={popoverOrigin}
      >
        <ButtonSetting handleClose={handleClose} />
        <MenuItem onClick={handleClickLogout}>{t('layout.header.menu.logout')}</MenuItem>
      </Menu>
      <InteractiveGuide
        open={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        steps={guideSteps}
        labels={{
          previous: t('layout.guide.actions.previous'),
          next: t('layout.guide.actions.next'),
          finish: t('layout.guide.actions.finish'),
          stepOf: (current, total) => t('layout.guide.stepOf', { current, total }),
        }}
      />
    </>
  );
};

export default MenuHeader;
